import { Box, Center, Flex, SimpleGrid, Stack } from '@mantine/core';

import { Button, InputLabel, MonthPicker, Select, TextInput, YesNoRadioButton } from 'components';
import { useCallback, useEffect, useState } from 'react';
import { CardRequestStep, CardResponse } from 'types';
import { AppUtils } from 'utils';

const CITY_DATA = require('../../../assets/data/cityData.json');
const POSTAL_DATA = require('../../../assets/data/postalData.json');

const FamilyInformationStep = ({
  form,
  onSubmit,
  options,
  t,
}: CardRequestStep) => {
  const [shouldSendCity, setShouldSendCity] = useState<boolean>(false);
  const [shouldSendPostal, setShouldSendPostal] = useState<boolean>(false);

  useEffect(() => {
    if (form.values.postal) {
      checkCity(form.values.postal, 'city');
    }
  }, [form.values.postal]);

  useEffect(() => {
    if (form.values.city) {
      checkPostal(form.values.city, 'postal');
    }
  }, [form.values.city]);

  function checkCity(postalValue: string, updateProperty: keyof CardResponse) {
    if (!shouldSendCity || AppUtils.isEmpty(postalValue) || postalValue.length < 4) {
      return;
    }
    const city = (POSTAL_DATA as Record<string, string>)[postalValue] || undefined;
    if (city) {
      setTimeout(() => {
        setShouldSendPostal(false);
        form.setFieldValue(updateProperty, city);
      }, 500);
    }
  }

  function checkPostal(cityValue: string, updateProperty: keyof CardResponse) {
    if (!shouldSendPostal || AppUtils.isEmpty(cityValue)) {
      return;
    }
    const postal = (CITY_DATA as Record<string, string>)[cityValue] || undefined;
    if (postal) {
      setTimeout(() => {
        setShouldSendCity(false);
        form.setFieldValue(updateProperty, postal);
      }, 500);
    }
  }

  const onSubmitWrapper = useCallback(() => {
    onSubmit();
  }, [form.values, options]);

  return (
    <Center>
      <Box w={'80%'}>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Stack>
            <Select
              label={t('input.country.label')}
              data={options['country'].map(el => (
                { value: el, label: t(`input.country.options.${el}`) }
              ))}
              key={form.key('country')}
              {...form.getInputProps('country')}
              clearable={true}
            />
            <Box>
              <InputLabel label={t('input.postalCity.label')} />
              <Flex direction={'row'} justify={'space-between'}>
                <TextInput
                  key={form.key('postal')}
                  {...form.getInputProps('postal')}
                  placeholder={t('input.postal.label')}
                  maxLength={4}
                  onChange={(event) => {
                    setShouldSendPostal(true);
                    setShouldSendCity(true);
                    form.setFieldValue('postal', event.currentTarget.value);
                  }}
                />
                <TextInput
                  key={form.key('city')}
                  {...form.getInputProps('city')}
                  placeholder={t('input.city.label')}
                  onChange={(event) => {
                    setShouldSendPostal(true);
                    setShouldSendCity(true);
                    form.setFieldValue('city', event.currentTarget.value);
                  }}
                />
              </Flex>
            </Box>
            <Box>
              <InputLabel label={t('input.streetNumber.label')} />
              <Flex direction={'row'} justify={'space-between'}>
                <TextInput
                  key={form.key('street')}
                  {...form.getInputProps('street')}
                  placeholder={t('input.street.label')}
                />
                <TextInput
                  key={form.key('houseNumber')}
                  {...form.getInputProps('houseNumber')}
                  placeholder={t('input.houseNumber.label')}
                  onBeforeInput={(event: any) => {
                    const cursorPos = event.currentTarget.selectionStart || 0;
                    if (cursorPos === 0) {
                      const inputValue = (event as any).data;
                      if (inputValue && !/^[0-9]/.test(inputValue)) {
                        event.preventDefault();
                      }
                    }
                  }}
                />
              </Flex>
            </Box>
            <MonthPicker
              label={t('input.livingFrom.label')}
              key={form.key('livingFrom')}
              {...form.getInputProps('livingFrom')}
              minDate={form.values.birthDate}
              maxDate={new Date()}
            />
          </Stack>
          <Stack>
            <Select
              label={t('input.houseSituation.label')}
              data={options['houseSituation'].map(el => (
                { value: el, label: t(`input.houseSituation.options.${el}`) }
              ))}
              key={form.key('houseSituation')}
              {...form.getInputProps('houseSituation')}
              clearable={true}
            />
            <Select
              label={t('input.maritalStatus.label')}
              data={options['maritalStatus'].map(el => (
                { value: el, label: t(`input.maritalStatus.options.${el}`) }
              ))}
              key={form.key('maritalStatus')}
              {...form.getInputProps('maritalStatus')}
              clearable={true}
            />
            <YesNoRadioButton
              name={'hasChildren'}
              label={t('input.hasChildren.label')}
              t={t}
              form={form}
            />
            {form.values.hasChildren &&
              <TextInput
                label={t('input.childrenBirthYear.label')}
                key={form.key('childrenBirthYear')}
                {...form.getInputProps('childrenBirthYear')}
              />
            }
          </Stack>
        </SimpleGrid>
        <Button mt={'lg'} onClick={onSubmitWrapper}>
          {t('action.next')}
        </Button>
      </Box>
    </Center>
  )
}

export default FamilyInformationStep;